/* eslint-disable max-lines */
import { isEmpty } from 'lodash';
import { createMiddleware } from '@shared/redux';
import {
    GET_USER_CONFIG_REQUEST_FAILED,
    GET_USER_CONFIG_REQUEST_SUCCEEDED,
    editUserConfig,
    getUserConfigData,
} from '@domain/user-config';
import { getObjectSelected } from '@domain/object-selected';
import { UserConfigData } from '@domain/user-config/user-config.types';
import { showWarningToast } from '@shared/toasts';
import {
    updateActiveFilter,
    updateSavedFilter,
    loadFilters,
    loadFiltersFailed,
    selectNewFilter,
    selectCustomFilter,
    saveCustomFilter,
    setUnnamedFilter,
    setCustomFilter,
    createGetFiltersState,
    SELECT_CUSTOM_FILTER,
    VALIDATE_ACTIVE_FILTER_CACHED_CAMPAIGNS,
    validateActiveFilterCachedCampaigns,
} from '@domain/campaigns/campaigns-filter/filters/filters.state';
import { isDataLoading } from '@shared/axios';
import { GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED } from '@domain/dashboard/campaigns/campaigns.state';
import { getFilteredCampaigns, createCampaignsSelector } from '../campaigns-filter.state';
import {
    closeSaveModal,
    disableClearRules,
    disableOpenSaveModal,
    ON_CHANGE_CUSTOM_FILTERS,
    ON_CLICK_CLEAR,
    SAVE_FILTER,
} from '../interface/interface.state';
import {
    APPLY_RULE,
    REMOVE_RULE,
    getCampaignsFilterRules,
} from '../rules/rules.state';
import { FilterRule } from '../rules/rules.types';
import { NEW_FILTER, DUPLICATE_FILTER_NAME_ERROR, UNNAMED_FILTER } from './filters.constants';
import { CustomFilters } from './filters.types';
import {
    buildSaveNewFilterUserConfig,
    buildUpdateActiveFilterUserConfig,
    buildUpdateSavedFilterUserConfig,
    findCustomFilter,
    isNewFilter,
    isSameFilter,
} from './filters.utils';

const mustValidateCachedCampaigns = [
    GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED,
    SELECT_CUSTOM_FILTER,
];

export const updateActiveFilterUserConfig = ({
    selectedFilter,
    userConfigData,
    type,
    id,
}) => (
    editUserConfig(
        buildUpdateActiveFilterUserConfig({
            userConfigData,
            type,
            id,
            selectedFilter,
        }),
    )
);

const isFilterNameAlreadyUsed = ({ customFilters, filterName }) => {
    const usedNames = customFilters
        .map(filter => filter.name.toLowerCase());

    return usedNames.includes(filterName.toLowerCase());
};

const cacheIsValid = (fresh, cached) => (
    fresh.length === cached?.length
    && fresh.every(hasCampaign(cached))
);

const hasCampaign = campaigns => campaign => campaigns.find(
    it => it.campaign_id === campaign.campaign_id,
);

const formatCampaignsForCache = campaigns => campaigns.map(campaign => ({
    platform: campaign.platform,
    campaign_id: campaign.campaign_id,
}));

const getNextFilterAction = (
    customFilters: CustomFilters,
    rules: Array<FilterRule>,
    campaigns,
    userConfigData: UserConfigData,
    objectSelected,
) => {
    const { type, id } = objectSelected;
    const savedFilter = customFilters.find(filter => isSameFilter(filter, { rules }));

    if (savedFilter) {
        return [
            setCustomFilter({
                filter: savedFilter,
                type,
                id,
            }),
            disableOpenSaveModal(),
            updateActiveFilterUserConfig({
                userConfigData,
                type,
                id,
                selectedFilter: savedFilter,
            }),
        ];
    }

    return [
        setUnnamedFilter({
            type, id, rules, campaigns,
        }),
        updateActiveFilterUserConfig({
            userConfigData,
            type,
            id,
            selectedFilter: {
                name: UNNAMED_FILTER,
                campaigns,
                rules,
            },
        }),
    ];
};

export const filtersMiddleware = createMiddleware({
    [SAVE_FILTER]: (state, action) => {
        const userConfigData = getUserConfigData(state);
        const objectSelected = getObjectSelected(state);

        const { type, id } = objectSelected;
        const { filterName } = action.payload;

        const filteredCampaigns = getFilteredCampaigns(state);
        const formattedCampaigns = formatCampaignsForCache(filteredCampaigns?.data);

        const newFilter = {
            name: filterName,
            rules: getCampaignsFilterRules(state),
            campaigns: formattedCampaigns,
        };

        const getFilters = createGetFiltersState(type, id);
        const filters = getFilters(state);

        if (isFilterNameAlreadyUsed({ customFilters: filters.customFilters, filterName })) {
            return [showWarningToast({ message: DUPLICATE_FILTER_NAME_ERROR })];
        }

        return [
            editUserConfig(
                buildSaveNewFilterUserConfig({
                    userConfigData, type, id, newFilter,
                }),
            ),
            saveCustomFilter({ newFilter, type, id }),
            closeSaveModal(),
        ];
    },
    [ON_CHANGE_CUSTOM_FILTERS]: (state, action) => {
        const userConfigData = getUserConfigData(state);
        const objectSelected = getObjectSelected(state);

        const { type, id } = objectSelected;

        const getFiltersState = createGetFiltersState(type, id);
        const filters = getFiltersState(state);

        if (isNewFilter(action.payload.selectedFilter)) {
            return [
                selectNewFilter({ type, id }),
                updateActiveFilterUserConfig({
                    userConfigData,
                    type,
                    id,
                    selectedFilter: NEW_FILTER,
                }),
            ];
        }

        return [
            selectCustomFilter({
                selectedFilter: action.payload.selectedFilter, type, id,
            }),
            updateActiveFilterUserConfig({
                userConfigData,
                type,
                id,
                selectedFilter: findCustomFilter(filters, action.payload),
            }),
        ];
    },
    [GET_USER_CONFIG_REQUEST_SUCCEEDED]: (state) => {
        const userConfigData = getUserConfigData(state);

        return loadFilters(userConfigData.CAMPAIGNS_FILTERS);
    },
    [GET_USER_CONFIG_REQUEST_FAILED]: () => loadFiltersFailed(),
    [APPLY_RULE]: (state) => {
        const userConfigData = getUserConfigData(state);
        const objectSelected = getObjectSelected(state);

        const { type, id } = objectSelected;
        const getFiltersState = createGetFiltersState(type, id);
        const { customFilters } = getFiltersState(state);
        const rules = getCampaignsFilterRules(state);

        const filteredCampaigns = getFilteredCampaigns(state);
        const formattedCampaigns = formatCampaignsForCache(filteredCampaigns?.data);

        return getNextFilterAction(
            customFilters,
            rules,
            formattedCampaigns,
            userConfigData,
            objectSelected,
        );
    },
    [REMOVE_RULE]: (state) => {
        const userConfigData = getUserConfigData(state);
        const rules = getCampaignsFilterRules(state);

        const objectSelected = getObjectSelected(state);
        const { type, id } = objectSelected;

        const getFiltersState = createGetFiltersState(type, id);

        if (rules.length === 0) {
            return [
                selectNewFilter({ type, id }),
                disableClearRules(),
                disableOpenSaveModal(),
                updateActiveFilterUserConfig({
                    userConfigData,
                    type,
                    id,
                    selectedFilter: NEW_FILTER,
                }),
            ];
        }

        const { customFilters } = getFiltersState(state);
        const filteredCampaigns = getFilteredCampaigns(state);
        const campaigns = formatCampaignsForCache(filteredCampaigns?.data);

        return getNextFilterAction(
            customFilters,
            rules,
            campaigns,
            userConfigData,
            objectSelected,
        );
    },
    [ON_CLICK_CLEAR]: (state) => {
        const userConfigData = getUserConfigData(state);
        const objectSelected = getObjectSelected(state);
        const { type, id } = objectSelected;

        return [
            selectNewFilter({ type, id }),
            updateActiveFilterUserConfig({
                userConfigData,
                type,
                id,
                selectedFilter: NEW_FILTER,
            }),
        ];
    },
    [mustValidateCachedCampaigns.toString()]: () => validateActiveFilterCachedCampaigns(),
    [VALIDATE_ACTIVE_FILTER_CACHED_CAMPAIGNS]: (state) => {
        const { type, id } = getObjectSelected(state);
        const getFilters = createGetFiltersState(type, id);
        const { activeFilter, customFilters } = getFilters(state);

        const getFilteredCampaignsByActiveFilterRules = createCampaignsSelector(activeFilter.rules);
        const freshFilteredCampaigns = getFilteredCampaignsByActiveFilterRules(state);
        const cachedCampaigns = activeFilter.campaigns;

        const isFilterActive = !isEmpty(activeFilter.rules);
        const areCampaignsLoading = isDataLoading(freshFilteredCampaigns.loadState);
        const isCacheValid = cacheIsValid(freshFilteredCampaigns.data, cachedCampaigns);

        const shouldEditFilters = isFilterActive
            && !areCampaignsLoading
            && !isCacheValid;

        if (!shouldEditFilters) {
            return [];
        }

        const updatedActiveFilter = {
            ...activeFilter,
            campaigns: formatCampaignsForCache(freshFilteredCampaigns.data),
        };

        const filterIsSaved = customFilters.find(filter => isSameFilter(filter, activeFilter));
        if (!filterIsSaved) {
            return [
                updateActiveFilter({
                    type,
                    id,
                    activeFilter: updatedActiveFilter,
                }),
                updateActiveFilterUserConfig({
                    type,
                    id,
                    selectedFilter: updatedActiveFilter,
                    userConfigData: getUserConfigData(state),
                }),
            ];
        }

        return [
            updateSavedFilter({
                type,
                id,
                savedFilter: updatedActiveFilter,
            }),
            editUserConfig(
                buildUpdateSavedFilterUserConfig({
                    state,
                    type,
                    id,
                    savedFilter: updatedActiveFilter,
                }),
            ),
        ];
    },
});
